import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import Pagination from '@/trio-ui/Pagination';
import * as Styles from './DataTable.styles';

/**
 * Column definition for the DataTable component.
 *
 * @template T - The type of data each row represents.
 */
export type Column<T> = {
  /** Unique identifier for the column. */
  id: string;
  /** Header label displayed in the table head. */
  label: string;
  /** Render function that returns the cell content for a given row. */
  render: (row: T) => React.ReactNode;
  /** Horizontal alignment of the column content. Defaults to 'left'. */
  align?: 'left' | 'center' | 'right';
  /** Fixed width for the column (number for px, string for any CSS unit). */
  width?: number | string;
};

/**
 * Props for the DataTable component.
 *
 * @template T - The type of data each row represents.
 */
export type DataTableProps<T> = {
  /** Column definitions describing header labels and cell renderers. */
  columns: Column<T>[];
  /** The data array to display. */
  data: T[];
  /** Show a loading spinner instead of the table. */
  isLoading?: boolean;
  /** Placeholder text for the search input. Omit to hide search. */
  searchPlaceholder?: string;
  /** Function that returns a searchable string for a given row. Required when searchPlaceholder is set. */
  searchFields?: (row: T) => string;
  /** Message displayed when the data array is empty. */
  emptyMessage?: string;
  /** Icon component displayed in the empty state. */
  emptyIcon?: React.ElementType;
  /** Function that returns a unique string key for a given row. */
  rowKey: (row: T) => string;
  /** Callback fired when a row is clicked. */
  onRowClick?: (row: T) => void;
  /** Enable row selection with checkboxes. */
  selectable?: boolean;
  /** The set of currently selected row IDs (controlled). */
  selectedIds?: Set<string>;
  /** Callback fired when the selection changes. */
  onSelectionChange?: (ids: Set<string>) => void;
  /** Content rendered inside the selection toolbar when rows are selected. */
  selectionActions?: React.ReactNode;
  /** Options for the rows-per-page dropdown. Defaults to [25, 50, 100]. */
  rowsPerPageOptions?: number[];
};

/**
 * A generic, reusable data table with optional search, pagination,
 * row selection with checkboxes, empty state, and loading state.
 *
 * Built on trio-ui Table components for consistent styling across all modules.
 */
function DataTable<T>({
  columns,
  data,
  isLoading = false,
  searchPlaceholder,
  searchFields,
  emptyMessage = 'No data found.',
  emptyIcon: EmptyIcon,
  rowKey,
  onRowClick,
  selectable = false,
  selectedIds,
  onSelectionChange,
  selectionActions,
}: DataTableProps<T>): JSX.Element {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [search, setSearch] = useState('');

  // Internal selection state used only when not controlled externally.
  const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set());
  const selected = selectedIds ?? internalSelected;
  const setSelected = onSelectionChange ?? setInternalSelected;

  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const filteredData = useMemo(() => {
    if (!search.trim() || !searchFields) return data;
    const q = search.toLowerCase();
    return data.filter((row) => searchFields(row).toLowerCase().includes(q));
  }, [data, search, searchFields]);

  const lastPage = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));

  const paginatedData = useMemo(
    () => filteredData.slice((page - 1) * itemsPerPage, page * itemsPerPage),
    [filteredData, page, itemsPerPage],
  );

  const allOnPageSelected = paginatedData.length > 0 && paginatedData.every((r) => selected.has(rowKey(r)));
  const someOnPageSelected = paginatedData.some((r) => selected.has(rowKey(r))) && !allOnPageSelected;

  // Set indeterminate state on the header checkbox (can only be done via ref).
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someOnPageSelected;
    }
  }, [someOnPageSelected]);

  const toggleAll = useCallback(() => {
    const next = new Set(selected);
    if (allOnPageSelected) {
      paginatedData.forEach((r) => next.delete(rowKey(r)));
    } else {
      paginatedData.forEach((r) => next.add(rowKey(r)));
    }
    setSelected(next);
  }, [selected, allOnPageSelected, paginatedData, rowKey, setSelected]);

  const toggleOne = useCallback(
    (id: string) => {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelected(next);
    },
    [selected, setSelected],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setPage(1);
  };

  // --- Loading state ---
  if (isLoading) {
    return (
      <Styles.LoadingState>
        <Styles.Spinner />
      </Styles.LoadingState>
    );
  }

  // --- Empty state ---
  if (data.length === 0) {
    return (
      <Styles.EmptyState>
        {emptyMessage}
      </Styles.EmptyState>
    );
  }

  const totalCols = columns.length + (selectable ? 1 : 0);

  const renderHeader = (
    <Tr>
      {selectable && (
        <Th width="40px" textAlign="center">
          <Styles.CheckboxCell>
            <Styles.HiddenCheckbox
              ref={headerCheckboxRef}
              checked={allOnPageSelected}
              onChange={toggleAll}
            />
          </Styles.CheckboxCell>
        </Th>
      )}
      {columns.map((col) => (
        <Th
          key={col.id}
          textAlign={col.align}
          width={col.width != null ? (typeof col.width === 'number' ? `${col.width}px` : col.width) : undefined}
        >
          {col.label}
        </Th>
      ))}
    </Tr>
  );

  const renderRow = (row: T, index: number) => {
    const id = rowKey(row);
    const isSelected = selected.has(id);
    return (
      <Tr
        key={id}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        style={{
          cursor: onRowClick ? 'pointer' : 'default',
          backgroundColor: isSelected ? '#EEF2FC' : undefined,
        }}
      >
        {selectable && (
          <Td width="40px" textAlign="center">
            <Styles.CheckboxCell>
              <Styles.HiddenCheckbox
                checked={isSelected}
                onChange={() => toggleOne(id)}
                onClick={(e) => e.stopPropagation()}
              />
            </Styles.CheckboxCell>
          </Td>
        )}
        {columns.map((col) => (
          <Td key={col.id} textAlign={col.align}>
            {col.render(row)}
          </Td>
        ))}
      </Tr>
    );
  };

  return (
    <Styles.Wrapper>
      {/* Search */}
      {searchPlaceholder && (
        <Styles.SearchBox>
          <Styles.SearchIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </Styles.SearchIcon>
          <Styles.SearchInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={handleSearchChange}
          />
        </Styles.SearchBox>
      )}

      {/* Selection toolbar */}
      {selectable && selected.size > 0 && (
        <Styles.SelectionBar>
          <Styles.SelectionLabel>
            {selected.size} selected
          </Styles.SelectionLabel>
          {selectionActions}
        </Styles.SelectionBar>
      )}

      {/* Count label */}
      {selected.size === 0 && (
        <Styles.CountLabel>
          {search
            ? `${filteredData.length} of ${data.length} results`
            : `${data.length} result${data.length !== 1 ? 's' : ''}`}
        </Styles.CountLabel>
      )}

      {/* Table */}
      <Table
        data={paginatedData}
        renderHeader={renderHeader}
        renderRow={renderRow}
        renderEmptyState={
          <Tr>
            <Td colSpan={totalCols} textAlign="center">
              {search ? `No results for "${search}"` : emptyMessage}
            </Td>
          </Tr>
        }
      />

      {/* Pagination */}
      {filteredData.length > 0 && (
        <Styles.PaginationWrapper>
          <Pagination
            count={filteredData.length}
            currentPage={page}
            itemsPerPage={itemsPerPage}
            lastPage={lastPage}
            onChangePageHandler={handlePageChange}
            onChangeRecordsPerPage={handleItemsPerPageChange}
          />
        </Styles.PaginationWrapper>
      )}
    </Styles.Wrapper>
  );
}

export default DataTable;
